import { Component } from '@angular/core';
import { listingsFeature } from '../../store/listings.store';
import { ListingPublic } from '../../../../globalTypes/listings.types';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { ListingService } from '../../services/listing.service';
import { filter, lastValueFrom, take, takeUntil } from 'rxjs';
import { Base } from '../../component/wrapper/base-wrapper.component';
import { FormsModule } from '@angular/forms';
import { ButtonComponent } from '../../component/layout/element/button.component';
import { ConfirmDeleteDialogComponent, ConfirmDeleteDialogData } from '../../helper/confirm.delete.dialog';
import { MatDialog } from '@angular/material/dialog';
import { Logger } from '../../helper/logger';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ListingsCardComponent } from "../../component/layout/element/listing-card.component";
import { accountFeature } from '../../store/account.store';
import { LocationPublic } from '../../../../globalTypes/account.types';
import { LoadingLogoComponent } from "../../component/layout/element/loading-logo.component";
const log = new Logger('DeliveryComponent');
@Component({
  selector: 'msh-status',
  imports: [ButtonComponent, FormsModule, ListingsCardComponent, LoadingLogoComponent],
  template: `
<div class="flex flex-col gap-3 h-full">
  <div class="flex justify-center mb-8">
    @if(displayListing && locationId && locations){
      <div class="grow"></div>
      <msh-listings-card [isPreviewOnly]="true" [locations]="locations" [locationId]="locationId" [listing]="displayListing" [verifiedLocationNoun]="verifiedLocationNoun"></msh-listings-card>
      <div class="grow"></div>
    } @else {
      <msh-loading-logo class="h-36 w-36"></msh-loading-logo>
    }
  </div>
  @if(warnings.length > 0){
    <div class="text-orange-500 bg-text/30 px-12 py-6 rounded-xl m-4 border border-orange-500">
      @for(warning of warnings; track warning){
        <div>{{warning}}</div>
      }
    </div>
  }
  @if(errors.length > 0){
    <div class="text-red-500 bg-text/30 px-12 py-6 rounded-xl m-4 border border-red-500">
      @for(error of errors; track error){
        <div>{{error}}</div>
      }
    </div>
  }
  <msh-button (onClick)="onPostClick($event)" [disabled]="errors.length > 0 || !listing" [isLoading]="isLoading" cta="Post Listing"></msh-button>
  <msh-button overrideClass="dull" [disabled]="errors.length > 0 || !listing" (onClick)="onPendingClick($event)" [isLoading]="isLoading" cta="Save As Pending"></msh-button>
  <div class="grow"></div>
  @if(listing && listing.id !== "new" && !isLoading){
    <msh-button overrideClass="warn" [disabled]="!listing" (onClick)="onDeleteClick($event)" [isLoading]="isLoading" cta="Delete Listing"></msh-button>
  }
</div>
  `,
  styles: `:host { display: flex; flex-direction: column; flex-grow: 1; height: 100%;}`
})
export class StatusComponent extends Base<StatusComponent> {
  listing: ListingPublic | Partial<ListingPublic> | null = null;
  displayListing: ListingPublic | null = null;
  shippingPrice?:number
  isLoading = false;
  multiplesAvailable = false;
  cta = 'Update';
  locationId = '';
  warnings: string[] = [];
  errors: string[] = [];
  locations: {[locationId: string]: LocationPublic} = {};
  verifiedLocationNoun?: string

  constructor(
    private router: Router,
    private store: Store,
    private snackbar: MatSnackBar,
    private listingService: ListingService,
    private dialog: MatDialog,
  ){ super(); 
    this.store.select(listingsFeature.selectListing).pipe(takeUntil(this.$destroy)).subscribe(listing=>{
      if(!listing) return; 
      this.listing = JSON.parse(JSON.stringify(listing));
      if(!this.listing || !this.listing?.id || this.listing.id === 'new') {
        this.cta = 'Next';
      }
      this.multiplesAvailable = !!this.listing && !!this.listing.listingVariations_on_listing
        && this.listing.listingVariations_on_listing.some(v => v.quantity && v.quantity > 1)
      this.evaluateListing();
    })
    this.store.select(accountFeature.selectSegment).pipe(takeUntil(this.$destroy)).subscribe(segment=>{
      const noun = segment?.verifiedLocationNoun + '';
      const capitalizedNoun = noun?.charAt(0).toUpperCase() + noun?.slice(1);
      this.verifiedLocationNoun = capitalizedNoun;
    });
  }

  async ngOnInit(): Promise<void> {}
  async evaluateListing(){
    if(!this.listing?.name) {
      this.errors.push("Listing information not found. Please retry.");
      return;
    }
    const isDigital = this.listing?.category?.isDigital;
    this.displayListing = JSON.parse(JSON.stringify(this.listing)) as ListingPublic;
    const {categoryId, name, description, listingVariations_on_listing, height, length, width, weight } = this.listing || {};
    // TODO add warnings for missing recommended fields eg weights are in feet or large
    if(!categoryId) this.errors.push("Details: Category is required")
    if(!name && !description) {
      this.errors.push("Details: Title and description are required")
    } else {
      if(!name) this.errors.push("Details: Title is required")
      if(!description) this.errors.push("Details: Description is required")
    }
    if(!listingVariations_on_listing || listingVariations_on_listing.length === 0) {
      this.errors.push("Price & Stock: At least one variation with price and stock is required")
    } else {
      listingVariations_on_listing.forEach((variation, index) => {
        variation.image?.url
        if(!variation.priceInCents || variation.priceInCents <= 0) this.errors.push(`Price & Stock: Variation ${index + 1} must have a price greater than $0.00`)
        if(!isDigital &&(!variation.quantity || variation.quantity <= 0)) this.errors.push(`Price & Stock: Variation ${index + 1} must have stock greater than 0`)
      })
      const hasPrice = listingVariations_on_listing.some(v => v.priceInCents && v.priceInCents > 0)
      const hasStock = listingVariations_on_listing.some(v => v.quantity && v.quantity > 0)
      if(!hasPrice) this.errors.push("Price & Stock: At least one variation must have a price greater than $0.00")
      if(!isDigital && !hasStock) this.errors.push("Price & Stock: At least one variation must have stock greater than 0")
    }
    if(!isDigital &&(!height || !length || !width || !weight)) this.errors.push("Delivery: Product dimensions and weight are required for shipping");
    this.locationId = (await lastValueFrom(this.store.select(accountFeature.selectLocation).pipe(filter(l=>!!l),take(1)))).id;
    this.locations = (await lastValueFrom(this.store.select(accountFeature.selectLocations).pipe(filter(l=>!!l),take(1))));
  }
  async onPostClick(ev: any){
    console.log({ev});
    if(!this.listing) throw new Error('Listing not found');
    this.isLoading = true; 
    this.listing.statusId = 'active';
    await this.saveListingStatus();
    this.isLoading = false;
  }
  async onPendingClick(ev: any){
    console.log({ev});
    if(!this.listing) throw new Error('Listing not found');
    this.isLoading = true;
    this.listing.statusId = 'pending';
    await this.saveListingStatus();
    this.isLoading = false;
  }
  async onDeleteClick(ev: any){
    console.log({ev});
    if(!this.listing) throw new Error('Listing not found');
    const data: ConfirmDeleteDialogData = {
      action: "Delete",
      item: "Listing",
      info: ["Are you sure you want","to delete this listing?"],
      class: '',
    }
    const dialog = this.dialog.open(ConfirmDeleteDialogComponent, {data});
    const results = await lastValueFrom(dialog.afterClosed().pipe(take(1)));
    if(!results) return;
    this.isLoading = true;
    this.listing.statusId = 'removed';
    await this.saveListingStatus();
    this.isLoading = false;
  }

  async saveListingStatus(){
    if(!this.listing) throw new Error('Listing not found');
    const isNewListing = this.listing.id === 'new';
    console.log({listing: this.listing})
    let nextPage = '';
    if(!this.listing.name) nextPage = 'details';
    if(!this.listing.lowestPrice) nextPage = 'price-stock';
    if(nextPage){
      this.router.navigate(['manage-listing', nextPage, this.listing?.id])
      return;
    }
    try {
      await this.listingService.saveListing(this.listing);
    } catch(error: any){
      log.error(error);
      this.isLoading = false;
      this.snackbar.open("Error encountered while "+(this.listing.statusId === "active" ? " publishing listing" : " updating the status"), "close", {duration: 8000})
      throw new Error("Error saving listing: " + error.message || "Unknown error");
    }
    if(this.listing.statusId !== "removed"){
      this.snackbar.open("Listing "+(this.listing.statusId === "active" ? " has been published" : "status updated"), "close", {duration: 8000})
    }
    setTimeout(()=>{
      // TODO There is a flash of the new listing last, then first... maybe don't fake a hydrate...
      this.listingService.loadListings();
      this.router.navigate(['/listing','search'])
    },0);
  }
}
